import React, { useState } from 'react';
import { Ban } from 'lucide-react';
import * as miniLeaguesApi from '../../api/miniLeagues';

/**
 * The manager calling their own league off.
 *
 * Sits beside "step down as manager" and is the opposite decision. Stepping down is for a
 * manager who wants out of a league that is still going, so the backend refuses it while a
 * game is under way — the point is to hand over cleanly. This one ends the league itself, so
 * games under way are no obstacle: they are cancelled with it.
 *
 * The reason is required and is not a formality. It goes on every game that gets cancelled,
 * which is where most members will actually meet it — the league page is not the only way in.
 *
 * Two steps rather than a window.confirm, because a confirm dialog cannot take the reason and
 * asking for it afterwards means the destructive click came first.
 */
function LeagueCancel({ league, isManager, onChanged }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Nothing to offer on a league that has already ended, either way.
  if (!isManager || league.complete || league.cancelled) return null;

  const submit = async (event) => {
    event.preventDefault();
    if (!reason.trim()) {
      setError('Say why — the other members will see this.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      await miniLeaguesApi.cancelLeague(league.id, reason.trim());
      setOpen(false);
      setReason('');
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.name === 'ApiError' ? (err.body?.error || 'That did not work') : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="league-manager-actions">
        <button className="secondary-btn" onClick={() => setOpen(true)}>
          <Ban size={16} />
          Cancel this league
        </button>
      </div>
    );
  }

  return (
    <form className="league-manager-actions league-cancel-form" onSubmit={submit}>
      <p className="league-cancel-warning">
        This ends the league for everyone. Any game still being played is cancelled with it, no
        further games will be created, and the join code stops working. It cannot be undone —
        starting again means a new league.
      </p>
      <label className="league-cancel-label" htmlFor="league-cancel-reason">
        Why are you cancelling?
      </label>
      <input
        id="league-cancel-reason"
        type="text"
        value={reason}
        // The backend's limit, not the column's: the reason is also written onto every live
        // game with a prefix, so anything longer used to fail the whole cancellation.
        maxLength={400}
        placeholder="Everyone has stopped picking"
        onChange={(e) => setReason(e.target.value)}
      />
      {error && <p className="error-text">{error}</p>}
      <div className="league-cancel-buttons">
        <button type="submit" className="primary-btn" disabled={busy}>
          {busy ? 'Cancelling…' : 'Yes, cancel the league'}
        </button>
        <button
          type="button"
          className="secondary-btn"
          disabled={busy}
          onClick={() => {
            setOpen(false);
            setError('');
          }}
        >
          Keep it going
        </button>
      </div>
    </form>
  );
}

export default LeagueCancel;
